import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { Users } from 'server/shared/collections/users';
import { randomId } from 'server/shared/helpers/random';
import { Customer } from 'server/shared/models/customer';
import { PoolerType } from 'server/shared/models/poolerType';
import { RoleEnum } from 'server/shared/models/role.enum';
import { DispoUser } from 'server/shared/models/user';
import { CustomerService } from '../../services/customer.service';
import { PermissionsService } from '../../services/permissions.service';
import { ToastService } from '../../services/toast/toast.service';
import { TranslationService } from '../../services/translation.service';
import { UserService } from '../../services/user.service';
import { ConfirmationDialog } from '../../shared/confirmation-dialog/confirmation.component';

interface PoolerTypeInCreation extends Omit<PoolerType, 'id'> {}
@Component({
  selector: 'admin-pooler-types',
  templateUrl: 'pooler-types.html',
  styleUrls: ['pooler-types.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoolerTypesComponent implements OnDestroy {
  i18n = this.translationService.getTranslations('poolerTypes');
  customer$ = new BehaviorSubject<Customer | undefined>(undefined);
  user$ = new BehaviorSubject<DispoUser | undefined>(undefined);

  currentUserTracker?: Tracker.Computation;
  private destroyed$ = new Subject<void>();

  poolerTypes$ = new BehaviorSubject<PoolerType[]>([]);
  initialPoolerTypes: PoolerType[] = [];
  poolerTypesInCreation$ = new BehaviorSubject<PoolerTypeInCreation[]>([]);

  poolerCountByType: Map<string, number> = new Map();

  constructor(
    private ngZone: NgZone,
    private location: Location,
    private toastService: ToastService,
    private translationService: TranslationService,
    private dialog: MatDialog,
    private customerService: CustomerService,
    private userService: UserService,
    private permissionService: PermissionsService,
  ) {
    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        this.customer$.next(customer);
        this.initializePoolerTypes();
      });
    this.permissionService.isCustomerAdmin$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCustomerAdmin) => {
        if (!isCustomerAdmin) {
          this.historyBack();
        }
      });
  }

  ngOnInit(): void {
    this.currentUserTracker = Tracker.autorun(() => {
      this.ngZone.run(() => {
        this.user$.next(
          Users.findOne({
            _id: Meteor.userId(),
            role: RoleEnum.DISPO,
          }) as DispoUser,
        );

        this.initializePoolerTypes();
      });
    });
  }

  initializePoolerTypes() {
    const customer = this.customer$.value;
    if (!customer) return;

    let poolerTypesFromCustomer = JSON.parse(
      JSON.stringify(customer.poolerTypes || []),
    );

    const currentPoolerTypes = this.poolerTypes$.value;
    if (currentPoolerTypes.length === 0) {
      this.poolerTypes$.next(
        JSON.parse(JSON.stringify(customer.poolerTypes || [])),
      );
    } else {
      const updatedPoolerTypes = [...currentPoolerTypes];
      poolerTypesFromCustomer.forEach((poolerTypeFromCustomer: PoolerType) => {
        let poolerTypeIndex = updatedPoolerTypes.findIndex((poolerType) => {
          return poolerType.id === poolerTypeFromCustomer.id;
        });
        if (poolerTypeIndex === -1) {
          updatedPoolerTypes.push(poolerTypeFromCustomer);
        }
        if (
          this.initialPoolerTypes[poolerTypeIndex] &&
          updatedPoolerTypes[poolerTypeIndex].displayName ===
            this.initialPoolerTypes[poolerTypeIndex].displayName
        ) {
          updatedPoolerTypes[poolerTypeIndex] = poolerTypeFromCustomer;
        }
      });
      this.poolerTypes$.next(updatedPoolerTypes);
    }
    this.initialPoolerTypes = poolerTypesFromCustomer;

    this.initialPoolerTypes.forEach((poolerType) => {
      let poolerTypeCount = Users.find({
        role: RoleEnum.POOL,
        [`customerInformation.${customer._id}.poolerType.id`]: poolerType.id,
      }).fetch().length;

      this.poolerCountByType.set(poolerType.id, poolerTypeCount);
    });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.currentUserTracker?.stop();
  }

  historyBack() {
    this.location.back();
  }

  addNewPoolerType() {
    const current = this.poolerTypesInCreation$.value;
    this.poolerTypesInCreation$.next([...current, { displayName: '' }]);
  }

  cancelNewPoolerType(index: number) {
    const current = this.poolerTypesInCreation$.value;
    const updated = [...current];
    updated.splice(index, 1);
    this.poolerTypesInCreation$.next(updated);
  }

  saveNewPoolerType(index: number) {
    let poolerTypeInCreation = this.poolerTypesInCreation$.value[index];
    const customer = this.customer$.value;
    if (!poolerTypeInCreation || !customer) return;
    let newPoolerType: PoolerType = {
      id: randomId(),
      displayName: poolerTypeInCreation.displayName,
      bookingWarningEnabled: poolerTypeInCreation.bookingWarningEnabled,
      bookingWarningMessage: poolerTypeInCreation.bookingWarningMessage,
    };

    // Update poolerTypes$
    const currentPoolerTypes = this.poolerTypes$.value;
    this.poolerTypes$.next([...currentPoolerTypes, newPoolerType]);

    // Update poolerTypesInCreation$
    const currentInCreation = this.poolerTypesInCreation$.value;
    const updatedInCreation = [...currentInCreation];
    updatedInCreation.splice(index, 1);
    this.poolerTypesInCreation$.next(updatedInCreation);
    Meteor.call(
      'addPoolerType',
      customer._id,
      newPoolerType,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  deletePoolerTypeDialog(poolerTypeId: string) {
    let count = this.poolerCountByType.get(poolerTypeId) || 0;

    if (count === 0) {
      this.deletePoolerType(poolerTypeId);
      return;
    }

    let dialogText =
      this.i18n.confirmPoolerTypeDeletion || 'Dienst wirklich ablehnen?';
    dialogText = dialogText.replace(
      '{{count}}',
      this.poolerCountByType.get(poolerTypeId) || 0,
    );

    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        id: 'deletePoolerTypeDialog',
        text: dialogText,
      },
    });
    dialogRef.afterClosed().subscribe((res: any) => {
      if (res) {
        this.deletePoolerType(poolerTypeId);
      }
    });
  }

  deletePoolerType(poolerTypeId: string) {
    const customer = this.customer$.value;
    if (!customer || !poolerTypeId) return;

    const currentPoolerTypes = this.poolerTypes$.value;
    let index = currentPoolerTypes.findIndex((poolerType) => {
      return poolerType.id === poolerTypeId;
    });
    const updatedPoolerTypes = [...currentPoolerTypes];
    updatedPoolerTypes.splice(index, 1);
    this.poolerTypes$.next(updatedPoolerTypes);

    Meteor.call(
      'removePoolerType',
      customer._id,
      poolerTypeId,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  cancelChanges(poolerTypeId: string) {
    let index = this.initialPoolerTypes.findIndex((poolerType) => {
      return poolerType.id === poolerTypeId;
    });

    const currentPoolerTypes = this.poolerTypes$.value;
    const updatedPoolerTypes = [...currentPoolerTypes];
    updatedPoolerTypes[index].displayName =
      this.initialPoolerTypes[index].displayName;
    this.poolerTypes$.next(updatedPoolerTypes);
  }

  savePoolerType(poolerTypeId: string): void {
    let poolerType = this.poolerTypes$.value.find((poolerType) => {
      return poolerType.id === poolerTypeId;
    });
    const customer = this.customer$.value;
    if (!customer || !poolerType) return;
    Meteor.call(
      'updatePoolerType',
      customer._id,
      poolerType,
      (error: Meteor.Error) => {
        if (error) {
          this.toastService.add({
            type: 'ERROR',
            message: error.error?.toString(),
          });
        } else {
          this.toastService.add({ type: 'SUCCESS', message: 'changesSaved' });
        }
      },
    );
  }

  trackById(index: number, object: PoolerType): string {
    return object.id;
  }
}
